import { createSelector } from '@reduxjs/toolkit';
import { get, identity, isEmpty, isNil, pipe, partition, zipAll, size, uniq } from 'lodash/fp';
import { DateTime } from 'luxon';
import {
    DateBin,
    ROI_CHART_KEY,
    QueryState,
    AssetRoiDimensions,
    ACCESS_TYPE
} from 'app/lib/constants';
import { transformDataset } from 'app/store/transformers.js';
import { rowsToColumns } from 'app/store/snowflakeQueries/utils.js';
import {
    generateTimeSeriesFromRange,
    getDateDomainRange,
    getDateRangeFromSelectedBars
} from 'app/lib/utils';
import { QueryKeys } from 'app/store/snowflakeQueries/reducer.js';
import {
    selectQueryMetadataForPropKey,
    selectQueryResultsForPropKey
} from 'app/store/snowflakeQueries/selectors.js';
import { selectIsUsageReportingEnabled } from 'app/store/applicationConfig/selectors.js';
import { selectUsageTableJob } from 'app/store/usageReporting/selectors';
import {
    getTableScore,
    getMetricsByTableFqn,
    transformMetricsResponse,
    getDimension,
    getAggregatedDataByBin,
    nextAggregationLevel,
    getSparklineTimeWindows,
    getMetric,
    sumObjectValues,
    padChartMetricsArray,
    getMeasure,
    getExplorationModel
} from './utils.js';

const selectSlice = get('ROIReporting');
export const selectSelectedTab = createSelector(selectSlice, get('selectedTab'));
export const selectIsAssetUsageDialogOpen = createSelector(selectSlice, get('isDialogOpen'));
const selectHasAccessToEventTable = createSelector(selectSlice, get('hasAccessToEventTable'));
export const selectIsCheckingEventTableAccess = createSelector(
    selectSlice,
    get('isCheckingEventTableAccess')
);
export const selectSelectedTableIndex = createSelector(selectSlice, get('selectedTableIndex'));
export const selectDateFilter = createSelector(selectSlice, get('dateFilter'));
export const selectDateBin = createSelector(selectSlice, get('dateBin'));
const selectedSparklineTimeWindows = createSelector(
    selectSlice,
    get('selectedSparklineTimeWindow')
);

export const selectCanAccessTableUsageReport = createSelector(
    selectHasAccessToEventTable,
    selectIsUsageReportingEnabled,
    selectUsageTableJob,
    (hasAccessToEventTable, isUsageReportingEnabled, job) =>
        Boolean(hasAccessToEventTable && isUsageReportingEnabled && !isNil(job))
);

export const selectAssetUsageTable = createSelector(selectSlice, get('selectedTable'));

const selectAllTableMetricsResult = createSelector(identity, (state) =>
    selectQueryResultsForPropKey(state, { queryKey: QueryKeys.ROI_REPORTING_ALL_TABLE_METRICS })
);

const selectFilteredTableMetricsResult = createSelector(identity, (state) =>
    selectQueryResultsForPropKey(state, {
        queryKey: QueryKeys.ROI_REPORTING_FILTERED_TABLE_METRICS
    })
);

export const selectAllTableMetricsMetadata = createSelector(identity, (state) =>
    selectQueryMetadataForPropKey(state, {
        queryKey: QueryKeys.ROI_REPORTING_ALL_TABLE_METRICS
    })
);

export const selectFilteredTableMetricsMetadata = createSelector(identity, (state) =>
    selectQueryMetadataForPropKey(state, {
        queryKey: QueryKeys.ROI_REPORTING_FILTERED_TABLE_METRICS
    })
);

const selectAllTableMetrics = createSelector(selectAllTableMetricsResult, transformMetricsResponse);

const selectFilteredTableMetrics = createSelector(
    selectFilteredTableMetricsResult,
    transformMetricsResponse
);

export const selectTableMetricsDimension = createSelector(
    selectAllTableMetricsResult,
    selectFilteredTableMetricsResult,
    selectDateBin,
    (all, filtered, bin) =>
        isEmpty(filtered) ? getDimension(all, bin) : getDimension(filtered, bin)
);

const selectAllTableMetricsByTableFqn = createSelector(selectAllTableMetrics, getMetricsByTableFqn);

export const selectROITableRecords = createSelector(
    selectAllTableMetricsByTableFqn,
    (allMetrics) => {
        if (isEmpty(allMetrics)) {
            return null;
        }
        const tableFqns = Object.keys(allMetrics);
        const records = tableFqns.map((table) => {
            const { writeVsReadScore, queryTimeVsCountScore } = getTableScore(allMetrics[table]);
            return [table, writeVsReadScore, queryTimeVsCountScore];
        });

        const data = {
            columns: [
                { name: 'ASSET_NAME', typeFamily: 'STRING' },
                { name: 'WRITE_VS_READ', typeFamily: 'DOUBLE' },
                { name: 'TIME_VS_COUNT', typeFamily: 'DOUBLE' }
            ],
            records,
            rowCount: records.length
        };
        return pipe(rowsToColumns, (d) => transformDataset(d))(data);
    }
);

export const selectSelectedTableFqn = createSelector(
    selectROITableRecords,
    selectSelectedTableIndex,
    (records, index) => records?.columns[0][index]
);

const selectAllMetricsForSelectedTable = createSelector(
    selectAllTableMetricsByTableFqn,
    selectSelectedTableFqn,
    (metrics, fqn) => {
        const [read, write] = partition({ ACCESS_TYPE: 'READ' }, metrics[fqn]);
        return { read, write };
    }
);

const selectPropKey = (_, props) => props.dataKey;
const isQueryMetrics = (key) =>
    key === ROI_CHART_KEY.AVG_QUERY_COUNT || key === ROI_CHART_KEY.AVG_QUERY_TIME;

const selectMetricsForSelectedTableAndPropKey = createSelector(
    selectPropKey,
    selectAllTableMetricsByTableFqn,
    selectSelectedTableFqn,
    selectFilteredTableMetrics,
    (key, allMetrics, fqn, filteredMetrics) => {
        const metrics = filteredMetrics ?? allMetrics[fqn] ?? [];
        const [read, write] = partition({ ACCESS_TYPE: 'READ' }, metrics);

        return isQueryMetrics(key) ? read : write;
    }
);

const getChartData = (data, key) => {
    if (!data) {
        return null;
    }
    if (isEmpty(data)) {
        return [];
    }

    return data?.map((entry) => [entry.TIMESTAMP ?? entry.WEEKDAY, getMetric(key, entry)]);
};

export const selectSparklineChartDataForPropKey = createSelector(
    selectAllMetricsForSelectedTable,
    selectDateBin,
    selectPropKey,
    (metrics, bin, key) => {
        const data = isQueryMetrics(key) ? metrics.read : metrics.write;
        if (isEmpty(data)) {
            return null;
        }
        const sparklineData =
            bin === DateBin.WEEKDAY || bin === DateBin.HOUR
                ? data
                : getAggregatedDataByBin(data, nextAggregationLevel[bin]);

        if (isEmpty(sparklineData) || sparklineData?.length < 2) {
            return null;
        }
        return getChartData(sparklineData, key);
    }
);

export const selectSparklineTimeWindowsForPropKey = createSelector(
    selectSparklineChartDataForPropKey,
    selectDateBin,
    (data, bin) => {
        if (isEmpty(data) || bin === DateBin.YEAR) {
            return [];
        }

        const xDomain = [data[0][0], data[data.length - 1][0]];
        const sparklineBin = nextAggregationLevel[bin];
        const windowSize = bin === DateBin.DAY ? DateBin.WEEK : nextAggregationLevel[sparklineBin];
        const units = bin === DateBin.DAY ? 2 : 1;
        return getSparklineTimeWindows(xDomain, windowSize, units);
    }
);

export const selectSelectedTimeWindowIndexForPropKey = createSelector(
    selectPropKey,
    selectedSparklineTimeWindows,
    selectSparklineTimeWindowsForPropKey,
    (key, selectedWindows, allWindows) =>
        isNil(selectedWindows[key]) ? allWindows.length - 1 : selectedWindows[key]
);

const selectSelectedTimeWindowForPropKey = createSelector(
    selectSelectedTimeWindowIndexForPropKey,
    selectSparklineTimeWindowsForPropKey,
    (selectedTimeWindowIndex, allWindows) => allWindows[selectedTimeWindowIndex]
);

export const selectMetricsForSelectedTimeWindowAndPropKey = createSelector(
    selectMetricsForSelectedTableAndPropKey,
    selectSelectedTimeWindowForPropKey,
    selectDateBin,
    selectPropKey,
    selectDateFilter,
    (metrics, timeWindow, bin, key, dateFilter) => {
        if (isEmpty(metrics)) {
            return generateTimeSeriesFromRange(dateFilter);
        }
        let result = metrics;
        if (!isEmpty(timeWindow)) {
            const { from, to } = timeWindow;
            result = metrics.filter(({ TIMESTAMP }) => TIMESTAMP >= from && TIMESTAMP <= to);
        }
        if (bin === DateBin.WEEKDAY) {
            result = getAggregatedDataByBin(result, DateBin.WEEKDAY);
        }
        const chartData = getChartData(result, key);
        if (!isEmpty(timeWindow)) {
            return padChartMetricsArray(chartData, timeWindow, bin);
        }
        return chartData;
    }
);

export const selectDashboardChartDateRange = createSelector(
    selectMetricsForSelectedTableAndPropKey,
    (data) => {
        if (isEmpty(data)) {
            return null;
        }
        const dates = data.map((item) => item.TIMESTAMP);
        return { min: DateTime.min(...dates).toJSDate(), max: DateTime.max(...dates).toJSDate() };
    }
);

export const selectMetricsForAssetUsageDialog = createSelector(
    selectMetricsForSelectedTableAndPropKey,
    selectPropKey,
    (data, key) => {
        const aggregatedData = getAggregatedDataByBin(data, DateBin.WEEKDAY);
        return getChartData(aggregatedData, key);
    }
);

export const selectTableScores = createSelector(selectFilteredTableMetrics, (metrics) => {
    if (isEmpty(metrics)) {
        return null;
    }
    return getTableScore(metrics);
});

export const selectTotalQueryMetricsForSelectedTable = createSelector(
    selectAllMetricsForSelectedTable,
    (metrics) => (metrics.read ? sumObjectValues(metrics.read, DateBin.DAY) : [])
);
export const selectTotalJobMetricsForSelectedTable = createSelector(
    selectAllMetricsForSelectedTable,
    (metrics) => (metrics.write ? sumObjectValues(metrics.write, DateBin.DAY) : [])
);

const selectAssetROIResult = createSelector(identity, (state) =>
    selectQueryResultsForPropKey(state, {
        queryKey: QueryKeys.ROI_REPORTING_ASSET_ROI
    })
);

export const selectAssetROIMetadata = createSelector(identity, (state) =>
    selectQueryMetadataForPropKey(state, {
        queryKey: QueryKeys.ROI_REPORTING_ASSET_ROI
    })
);

export const selectTableCubeDateBin = createSelector(selectSlice, get('tableCube.dateBin'));

export const selectAssetROIDimension = createSelector(
    selectAssetROIResult,
    selectTableCubeDateBin,
    getDimension
);

export const selectAssetROIMeasure = createSelector(selectAssetROIResult, getMeasure);

export const selectTableCubeSelectedMeasure = createSelector(
    selectSlice,
    get('tableCube.selectedMeasure')
);

export const selectTableCubePrimaryDimension = createSelector(
    selectSlice,
    get('tableCube.primaryDimension')
);

export const selectTableCubeSecondaryDimension = createSelector(
    selectSlice,
    get('tableCube.secondaryDimension')
);

export const selectTableCubeMeasure = createSelector(selectSlice, get('tableCube.selectedMeasure'));

export const selectTableCubeChartType = createSelector(selectSlice, get('tableCube.chartType'));

export const selectDateTimeCardinality = createSelector(selectAssetROIResult, (data) => {
    const dates = data?.columns[0]?.map((date) => date.valueOf());
    return size(uniq(dates));
});

export const selectTableCubeWarehouses = createSelector(identity, (state) => {
    const results = selectQueryResultsForPropKey(state, {
        queryKey: QueryKeys.ROI_TABLE_CUBE_WAREHOUSE_VALUES
    });
    return results?.columns[0];
});

export const selectTableCubeUsers = createSelector(identity, (state) => {
    const results = selectQueryResultsForPropKey(state, {
        queryKey: QueryKeys.ROI_TABLE_CUBE_USER_VALUES
    });
    return results?.columns[0];
});

export const selectTableCubeTables = createSelector(identity, (state) => {
    const results = selectQueryResultsForPropKey(state, {
        queryKey: QueryKeys.ROI_TABLE_CUBE_TABLE_VALUES
    });
    return results?.columns[0];
});

export const selectIsLoadingWarehousesList = createSelector(identity, (state) => {
    const metadata = selectQueryMetadataForPropKey(state, {
        queryKey: QueryKeys.ROI_TABLE_CUBE_WAREHOUSE_VALUES
    });
    return metadata.state === QueryState.RUNNING;
});

export const selectIsLoadingUsersList = createSelector(identity, (state) => {
    const metadata = selectQueryMetadataForPropKey(state, {
        queryKey: QueryKeys.ROI_TABLE_CUBE_USER_VALUES
    });
    return metadata.state === QueryState.RUNNING;
});

export const selectIsLoadingTablesList = createSelector(identity, (state) => {
    const metadata = selectQueryMetadataForPropKey(state, {
        queryKey: QueryKeys.ROI_TABLE_CUBE_TABLE_VALUES
    });
    return metadata.state === QueryState.RUNNING;
});

export const selectTableCubeDateFilter = createSelector(selectSlice, get('tableCube.dateFilter'));
export const selectTableCubeWarehouseFilter = createSelector(
    selectSlice,
    get('tableCube.warehouseFilter')
);
export const selectTableCubeUserFilter = createSelector(selectSlice, get('tableCube.userFilter'));
export const selectTableCubeTableFilter = createSelector(selectSlice, get('tableCube.tableFilter'));
export const selectTableCubeAccessTypeFilter = createSelector(
    selectSlice,
    get('tableCube.accessTypeFilter')
);

const getValues = (values, filteredValues) => (isEmpty(filteredValues) ? values : filteredValues);
const selectVisibleTableCubeWarehouses = createSelector(
    selectTableCubeWarehouses,
    selectTableCubeWarehouseFilter,
    getValues
);

const selectVisibleTableCubeUsers = createSelector(
    selectTableCubeUsers,
    selectTableCubeUserFilter,
    getValues
);

const selectVisibleTableCubeTables = createSelector(
    selectTableCubeTables,
    selectTableCubeTableFilter,
    getValues
);

const selectChartDataFromFilterValues = createSelector(
    selectTableCubePrimaryDimension,
    selectVisibleTableCubeUsers,
    selectVisibleTableCubeTables,
    selectTableCubeAccessTypeFilter,
    selectVisibleTableCubeWarehouses,
    (dimension, users, tables, accessType, warehouses) => {
        if (dimension === AssetRoiDimensions.DATE) {
            return null;
        }
        let data;
        if (dimension === AssetRoiDimensions.ACCESS_TYPE) {
            data = accessType ?? ACCESS_TYPE;
        } else if (dimension === AssetRoiDimensions.TABLE) {
            data = tables;
        } else if (dimension === AssetRoiDimensions.USER) {
            data = users;
        } else if (dimension === AssetRoiDimensions.WAREHOUSE) {
            data = warehouses;
        }
        return data.map((item) => [item, 0]);
    }
);

const selectPaddedResults = createSelector(
    selectAssetROIResult,
    selectTableCubeDateBin,
    selectTableCubeDateFilter,
    selectTableCubePrimaryDimension,
    (results, bin, dateFilter, dimension) => {
        if (isEmpty(results)) {
            return null;
        }
        if (dateFilter && dimension === AssetRoiDimensions.DATE) {
            const chartData = zipAll(results.columns);
            return padChartMetricsArray(
                chartData,
                {
                    from: DateTime.fromISO(dateFilter.rangeFrom),
                    to: DateTime.fromISO(dateFilter.rangeTo)
                },
                bin
            );
        }
        return zipAll(results.columns);
    }
);

const selectGeneratedChartDataFromFilters = createSelector(
    selectTableCubeDateFilter,
    selectTableCubePrimaryDimension,
    selectChartDataFromFilterValues,
    (dateFilter, dimension, dataFromFilterValues) =>
        dimension === AssetRoiDimensions.DATE
            ? generateTimeSeriesFromRange(dateFilter)
            : dataFromFilterValues
);

export const selectAssetROIData = createSelector(
    selectAssetROIResult,
    selectGeneratedChartDataFromFilters,
    selectPaddedResults,
    (results, generatedData, paddedResults) => {
        if (isNil(results)) {
            return null;
        }
        if (isEmpty(results.columns)) {
            return generatedData;
        }
        return paddedResults;
    }
);

export const selectSelectedChartArea = createSelector(
    selectSlice,
    get('tableCube.selectedChartArea')
);

export const selectTableCubeDateDomainRange = createSelector(
    selectAssetROIResult,
    selectTableCubePrimaryDimension,
    (result, dimension) => {
        if (dimension !== AssetRoiDimensions.DATE) {
            return null;
        }
        return getDateDomainRange(result);
    }
);

export const selectSelectedDashboardChartArea = createSelector(
    selectSlice,
    get('selectedDashboardChartArea')
);

const selectFilterFromSelectedArea = createSelector(
    selectSelectedChartArea,
    selectTableCubePrimaryDimension,
    selectTableCubeDateBin,
    (selection, dimension, dateBin) => {
        if (isEmpty(selection)) {
            return {};
        }
        if (dimension === AssetRoiDimensions.USER) {
            return { users: selection };
        }
        if (dimension === AssetRoiDimensions.WAREHOUSE) {
            return { warehouses: selection };
        }
        if (dimension === AssetRoiDimensions.TABLE) {
            return { tables: selection };
        }
        if (dimension === AssetRoiDimensions.ACCESS_TYPE) {
            return { accessType: selection };
        }
        return getDateRangeFromSelectedBars(selection, dateBin);
    }
);

const selectTableCubeStringFilters = createSelector(
    selectTableCubeWarehouseFilter,
    selectTableCubeUserFilter,
    (warehouseFilters, userFilters) => [
        {
            name: 'warehouses',
            filteredValues: warehouseFilters,
            columnName: 'WAREHOUSE_NAME'
        },
        { name: 'users', filteredValues: userFilters, columnName: 'USER_NAME' }
    ]
);

const selectTableCubeArrayFilters = createSelector(
    selectTableCubeTableFilter,
    selectTableCubeAccessTypeFilter,
    selectFilterFromSelectedArea,
    (tableFilter, accessTypeFilter, chartSelection) => {
        const filterValue = accessTypeFilter ?? chartSelection.accessType;
        if (isEmpty(filterValue)) {
            return null;
        }
        const columnName = filterValue.includes('WRITE') ? 'TARGET_TABLES' : 'SOURCE_TABLES';
        return { name: 'tables', filteredValues: tableFilter, columnName, type: 'ARRAY' };
    }
);

export const selectQueryDetailsInitialModel = createSelector(
    selectTableCubeDateFilter,
    selectTableCubeStringFilters,
    selectTableCubeArrayFilters,
    selectFilterFromSelectedArea,
    (dateFilters, stringFilters, arrayFilter, chartFilter) =>
        getExplorationModel({ dateFilters, stringFilters, arrayFilter, chartFilter })
);

export const selectEventsTableSchema = createSelector(selectSlice, get('eventsTableColumns'));
